import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { PrivilegeType } from '@/consts/team'

import { decodeJwtExp } from './CliTokenComponent'

interface NewCliTokenModalProps {
  onClose: () => void
}

export function NewCliTokenModal({ onClose }: NewCliTokenModalProps) {
  const { t } = useTranslation()
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    API.getPlatformToken({ privilege_type: PrivilegeType.CliToken })
      .then((res) => setToken(res.token))
      .finally(() => setLoading(false))
  }, [])

  const tokenInfo = token ? decodeJwtExp(token) : null

  const handleCopy = () => {
    navigator.clipboard.writeText(token)
    message.success(t('profile.cliToken.copied'))
  }

  return (
    <Modal className="w-[640px]" open={true} onClose={onClose}>
      <Modal.Header>{t('profile.cliToken.modalTitle')}</Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center items-center min-h-[88px]">
            <Loading />
          </div>
        ) : (
          <>
            <div className="border border-neutral-200 rounded-lg px-4 py-3 min-h-[88px] break-all t-sb text-primary">
              {token}
            </div>
            {tokenInfo && (
              <p className="mt-2 t-sm text-secondary">
                <span>{t('profile.cliToken.modalWarning')}</span>
                <span className="text-highlight">
                  {t('profile.cliToken.modalWarningHighlight', {
                    date: tokenInfo.expirationDate,
                    days: tokenInfo.daysRemaining,
                  })}
                </span>
                <span>{t('profile.cliToken.modalWarningAfter')}</span>
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          {t('profile.cliToken.modalCancel')}
        </Button>
        <Button type="secondary" disabled={loading} onClick={handleCopy} className="min-w-[88px]">
          {t('profile.cliToken.modalCopy')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
